import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  TrendingUp,
  Users,
  Target,
  Calendar,
  IndianRupee,
  Award,
  Loader2,
  FileText,
  ChevronRight,
  ChevronLeft,
  Clock,
  Building2,
  Eye,
  BarChart3,
  CheckCircle2,
  Phone,
  MessageCircle,
  PhoneOff,
  PhoneForwarded,
  X as XIcon
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { dashboardAPI, leadsAPI, incentivesAPI, dealsAPI, clientsAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { usePrivacy } from '../../contexts/PrivacyContext';
import { cn } from '../../lib/utils';

import { toast } from '../../hooks/use-toast';

// Plain formatter for non-masked values (exports, etc.)
const formatCurrencyPlain = (amount) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0
  }).format(amount || 0);
};

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// Employee Dashboard Component
const EmployeeDashboard = ({ stats, incentives, clients, teamConfigs, pendingCallbacks = [], onMarkCallbackDone, onRefreshCallbacks, canSeeIncentives = true, formatCurrency }) => {
  const navigate = useNavigate();
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [renewalDetailOpen, setRenewalDetailOpen] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(null);
  
  // Call Outcome Modal State
  const [callOutcomeOpen, setCallOutcomeOpen] = useState(false);
  const [selectedLeadForCall, setSelectedLeadForCall] = useState(null);
  const [callOutcome, setCallOutcome] = useState({
    contact_method: 'PHONE_CALL',
    outcome: '',
    callback_date: '',
    callback_time: '',
    callback_notes: '',
    notes: ''
  });
  const [isSubmittingCall, setIsSubmittingCall] = useState(false);

  // Handle recording call outcome
  const handleRecordCallOutcome = async () => {
    if (!selectedLeadForCall || !callOutcome.outcome) return;
    
    setIsSubmittingCall(true);
    try {
      const payload = {
        contact_method: callOutcome.contact_method,
        outcome: callOutcome.outcome,
        notes: callOutcome.notes
      };
      
      if (callOutcome.outcome === 'PICKED') {
        // If picked, the status will be updated based on API logic
      } else if (callOutcome.outcome === 'CALLBACK_LATER') {
        payload.callback_date = callOutcome.callback_date;
        payload.callback_time = callOutcome.callback_time;
        payload.callback_notes = callOutcome.callback_notes;
      }
      
      await leadsAPI.recordCallOutcome(selectedLeadForCall.id, payload);
      
      // Reset and close
      setCallOutcomeOpen(false);
      setSelectedLeadForCall(null);
      setCallOutcome({
        contact_method: 'PHONE_CALL',
        outcome: '',
        callback_date: '',
        callback_time: '',
        callback_notes: '',
        notes: ''
      });
      
      // Refresh callbacks
      if (onRefreshCallbacks) onRefreshCallbacks();
      
    } catch (error) {
      console.error('Failed to record call outcome:', error);
      toast({
        variant: 'destructive',
        title: 'Failed to record call outcome',
        description: error.response?.data?.detail || error.message
      });
    } finally {
      setIsSubmittingCall(false);
    }
  };

  // Calculate incentive stats from records
  const pendingApproval = incentives.filter(inc => inc.status === 'PENDING').reduce((sum, inc) => sum + (inc.amount_earned || 0), 0);
  const approved = incentives.filter(inc => inc.status === 'APPROVED').reduce((sum, inc) => sum + (inc.amount_earned || 0), 0);
  const readyForPayout = incentives.filter(inc => inc.status === 'PAYABLE').reduce((sum, inc) => sum + (inc.amount_earned || 0), 0);
  const totalPaid = incentives.filter(inc => inc.status === 'PAID').reduce((sum, inc) => sum + (inc.amount_paid || inc.amount_earned || 0), 0);
  const totalEarned = incentives.reduce((sum, inc) => sum + (inc.amount_earned || 0), 0);

  // Recent incentives (last 5)
  const recentIncentives = incentives.slice(0, 5);

  // Get renewal percentage from config
  const getRenewalPercentage = () => {
    const latestConfig = teamConfigs.sort((a, b) => b.effective_month?.localeCompare(a.effective_month || ''))[0];
    return latestConfig?.renewal_first_month_percentage || 5;
  };

  // Calculate projected renewal incentives for selected year
  const projectedRenewals = useMemo(() => {
    const renewalPct = getRenewalPercentage();
    const monthlyProjections = [];
    
    // Process each month of the selected year
    for (let month = 0; month < 12; month++) {
      const monthName = MONTHS[month];
      
      let monthTotal = 0;
      const clientBreakdown = [];
      
      clients.forEach(client => {
        // Skip if no close date
        if (!client.close_date) return;
        
        // Skip terminated clients (check pricing_revision_history or mrr = 0)
        const isTerminated = client.mrr === 0 || client.is_terminated;
        if (isTerminated) return;
        
        const closeDate = new Date(client.close_date);
        const closeMonth = closeDate.getMonth();
        const closeYear = closeDate.getFullYear();
        
        // Renewal logic:
        // 1. Must be a future year compared to close year (selectedYear > closeYear)
        // 2. Must be the anniversary month (month === closeMonth)
        // Count only paid/live deals (exclude payment pending)
        const isPaidOrLive = client.status === 'WON_PAYMENT_RECEIVED' || client.status === 'IMPLEMENTATION_IN_PROGRESS' || client.status === 'LIVE_ACTIVE';
        if (!isPaidOrLive) return;

        if (selectedYear > closeYear && month === closeMonth) {
            const clientMRR = client.mrr || client.plan_amount || 0;
            const renewalAmount = (clientMRR * renewalPct) / 100;
            
            if (renewalAmount > 0) {
                clientBreakdown.push({
                    company: client.company_name,
                    mrr: clientMRR,
                    renewalPct: renewalPct,
                    amount: renewalAmount
                });
                monthTotal += renewalAmount;
            }
        }
      });
      
      // Determine if this month is in the past/current relative to REAL time
      const realDate = new Date();
      const isPast = selectedYear < realDate.getFullYear() || (selectedYear === realDate.getFullYear() && month < realDate.getMonth());
      const isCurrent = selectedYear === realDate.getFullYear() && month === realDate.getMonth();
      
      monthlyProjections.push({
        month: month,
        monthName: `${monthName} ${selectedYear}`,
        amount: monthTotal,
        clients: clientBreakdown,
        isPast: isPast,
        isCurrent: isCurrent
      });
    }
    
    return monthlyProjections;
  }, [clients, selectedYear, teamConfigs]);

  const totalProjectedRenewals = projectedRenewals.reduce((sum, p) => sum + p.amount, 0);
  const renewalPct = getRenewalPercentage();

  // Employee 10 Year Projection
  const projectionData = useMemo(() => {
    // 1. Calculate Annual Renewal Liability per Client
    const renewalPct = teamConfigs.sort((a, b) => b.effective_month?.localeCompare(a.effective_month || ''))[0]?.renewal_first_month_percentage || 5;
    
    let annualLiability = 0;
    clients.forEach(client => {
        if (client.status === 'WON_PAYMENT_RECEIVED' || client.status === 'IMPLEMENTATION_IN_PROGRESS' || client.status === 'LIVE_ACTIVE') {
            const mrr = client.mrr || client.plan_amount || 0;
            // Employees get renewalPct of 1st month every year
            const yearlyPayout = (mrr * renewalPct) / 100;
            annualLiability += yearlyPayout;
        }
    });

    // 2. Generate 10 Year Table
    const currentYear = new Date().getFullYear();
    const tenYearProjection = [];
    
    // Start from Next Year
    for (let i = 1; i <= 10; i++) {
        const year = currentYear + i;
        tenYearProjection.push({
            year: year,
            amount: annualLiability
        });
    }

    return {
        nextYearAmount: annualLiability,
        table: tenYearProjection
    };
  }, [clients, teamConfigs]);

  // Available years for selector
  const availableYears = useMemo(() => {
    const currentYear = new Date().getFullYear();
    return [currentYear, currentYear + 1, currentYear + 2, currentYear + 3];
  }, []);

  const handleMonthClick = (monthData) => {
    setSelectedMonth(monthData);
    setRenewalDetailOpen(true);
  };

  return (
    <div className="space-y-5 sm:space-y-6">
      {/* Header Section */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-semibold text-slate-900 dark:text-slate-100">My Dashboard</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">Track your performance and manage your work</p>
        </div>
        {pendingCallbacks.length > 0 && (
          <Badge variant="outline" className="border-orange-300 bg-orange-50 text-orange-700 px-3 py-1.5 text-sm w-fit">
            <Clock className="w-4 h-4 mr-1.5" />
            {pendingCallbacks.length} Pending Callback{pendingCallbacks.length > 1 ? 's' : ''}
          </Badge>
        )}
      </div>

      {/* Pending Callbacks Section */}
      {pendingCallbacks.length > 0 && (
        <Card className="border-l-4 border-l-orange-500 bg-white dark:bg-slate-800">
          <CardHeader className="flex flex-row items-center justify-between pb-3 px-4 sm:px-6">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-orange-100 flex items-center justify-center">
                <Clock className="w-4 h-4 text-orange-600" />
              </div>
              <div>
                <CardTitle className="text-base font-medium text-slate-900 dark:text-slate-100">
                  Scheduled Callbacks
                </CardTitle>
                <p className="text-xs text-slate-500">{pendingCallbacks.length} call{pendingCallbacks.length > 1 ? 's' : ''} scheduled</p>
              </div>
            </div>
            <Button variant="ghost" size="sm" className="text-slate-600 hover:text-slate-900 text-sm" onClick={() => navigate('/leads?status=CALLBACK_SCHEDULED')}>
              View All
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </CardHeader>
          <CardContent className="px-4 sm:px-6 pb-4 sm:pb-6">
            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {pendingCallbacks.slice(0, 6).map((callback) => (
                <div 
                  key={callback.id} 
                  className={cn(
                    "flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-all hover:shadow-sm",
                    callback.is_overdue ? "bg-red-50 border-red-200 dark:bg-red-900/20" : 
                    "bg-slate-50 border-slate-200 dark:bg-slate-900 dark:border-slate-700"
                  )}
                  onClick={() => navigate(`/leads/${callback.id}`)}
                >
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className={cn(
                      "w-2 h-2 rounded-full flex-shrink-0",
                      callback.is_overdue ? "bg-red-500" : "bg-orange-500"
                    )} />
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-slate-900 dark:text-slate-100 truncate">{callback.company_name}</p>
                      <p className="text-xs text-slate-500 truncate">{callback.poc_name}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className={cn(
                      "text-xs font-medium px-2 py-0.5 rounded",
                      callback.is_overdue ? "text-red-700 bg-red-100" : "text-slate-600 bg-slate-100"
                    )}>
                      {callback.is_overdue ? 'Overdue' : callback.is_today ? 'Today' : new Date(callback.callback_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                    </span>
                    <Button 
                      variant="default" 
                      size="sm" 
                      className="h-8 px-3 bg-slate-900 hover:bg-slate-800 text-white text-xs"
                      onClick={(e) => { 
                        e.stopPropagation(); 
                        setSelectedLeadForCall(callback);
                        setCallOutcomeOpen(true);
                      }}
                    >
                      <Phone className="w-3.5 h-3.5 mr-1" />
                      Call
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Performance Metrics */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <Card className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700">
          <CardContent className="p-4 sm:p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wide">SQDs This Month</p>
                <p className="text-2xl sm:text-3xl font-semibold text-slate-900 dark:text-slate-100 mt-1">{stats.sqds_this_month || 0}</p>
              </div>
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center">
                <Target className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600 dark:text-blue-400" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700">
          <CardContent className="p-4 sm:p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wide">Deals Won</p>
                <p className="text-2xl sm:text-3xl font-semibold text-slate-900 dark:text-slate-100 mt-1">{stats.deals_won_this_month || 0}</p>
              </div>
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-emerald-50 dark:bg-emerald-900/30 flex items-center justify-center">
                <TrendingUp className="w-5 h-5 sm:w-6 sm:h-6 text-emerald-600 dark:text-emerald-400" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700">
          <CardContent className="p-4 sm:p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wide">Open Leads</p>
                <p className="text-2xl sm:text-3xl font-semibold text-slate-900 dark:text-slate-100 mt-1">{stats.open_leads || 0}</p>
              </div>
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-slate-100 dark:bg-slate-700 flex items-center justify-center">
                <FileText className="w-5 h-5 sm:w-6 sm:h-6 text-slate-600 dark:text-slate-400" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700">
          <CardContent className="p-4 sm:p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wide">Demos Scheduled</p>
                <p className="text-2xl sm:text-3xl font-semibold text-slate-900 dark:text-slate-100 mt-1">{stats.demos_scheduled || 0}</p>
              </div>
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-orange-50 dark:bg-orange-900/30 flex items-center justify-center">
                <Calendar className="w-5 h-5 sm:w-6 sm:h-6 text-orange-600 dark:text-orange-400" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions & Incentive Summary Row */}
      <div className="grid gap-4 sm:gap-5 lg:grid-cols-12">
        {/* Incentive Summary (hidden if incentives disabled) */}
        {canSeeIncentives && (
        <Card className="lg:col-span-8 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700">
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                <Award className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
              </div>
              <CardTitle className="text-base font-medium text-slate-900 dark:text-slate-100">
                Incentive Summary
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
              <div className="p-3 sm:p-4 bg-slate-50 dark:bg-slate-900 rounded-lg border border-slate-100 dark:border-slate-700">
                <p className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wide">Total Earned</p>
                <p className="text-lg sm:text-xl font-semibold text-slate-900 dark:text-slate-100 mt-1 truncate">{formatCurrency(totalEarned)}</p>
              </div>
              <div className="p-3 sm:p-4 bg-slate-50 dark:bg-slate-900 rounded-lg border border-slate-100 dark:border-slate-700">
                <p className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wide">Pending</p>
                <p className="text-lg sm:text-xl font-semibold text-orange-600 dark:text-orange-400 mt-1 truncate">{formatCurrency(pendingApproval)}</p>
              </div>
              <div className="p-3 sm:p-4 bg-slate-50 dark:bg-slate-900 rounded-lg border border-slate-100 dark:border-slate-700">
                <p className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wide">Approved</p>
                <p className="text-lg sm:text-xl font-semibold text-blue-600 dark:text-blue-400 mt-1 truncate">{formatCurrency(approved)}</p>
              </div>
              <div className="p-3 sm:p-4 bg-slate-50 dark:bg-slate-900 rounded-lg border border-slate-100 dark:border-slate-700">
                <p className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wide">Ready</p>
                <p className="text-lg sm:text-xl font-semibold text-slate-900 dark:text-slate-100 mt-1 truncate">{formatCurrency(readyForPayout)}</p>
              </div>
              <div className="col-span-2 sm:col-span-1 p-3 sm:p-4 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg border border-emerald-100 dark:border-emerald-800">
                <p className="text-xs font-medium text-emerald-600 dark:text-emerald-400 uppercase tracking-wide">Paid Out</p>
                <p className="text-lg sm:text-xl font-semibold text-emerald-700 dark:text-emerald-300 mt-1 truncate">{formatCurrency(totalPaid)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        )}
        
        {/* Quick Actions */}
        <Card className={cn("bg-slate-900 dark:bg-slate-950 border-0", canSeeIncentives ? "lg:col-span-4" : "lg:col-span-12")}>
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-medium text-white">Quick Actions</CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <div className={cn("grid gap-2", canSeeIncentives ? "grid-cols-2" : "grid-cols-4")}>
              <Button variant="outline" className="h-auto py-3 flex-col gap-1.5 bg-slate-800 border-slate-700 hover:bg-slate-700 text-white" onClick={() => navigate('/leads')}>
                <FileText className="w-5 h-5 text-slate-400" />
                <span className="text-xs font-medium">Leads</span>
              </Button>
              <Button variant="outline" className="h-auto py-3 flex-col gap-1.5 bg-slate-800 border-slate-700 hover:bg-slate-700 text-white" onClick={() => navigate('/demos')}>
                <Calendar className="w-5 h-5 text-slate-400" />
                <span className="text-xs font-medium">Demos</span>
              </Button>
              <Button variant="outline" className="h-auto py-3 flex-col gap-1.5 bg-slate-800 border-slate-700 hover:bg-slate-700 text-white" onClick={() => navigate('/deals')}>
                <Target className="w-5 h-5 text-slate-400" />
                <span className="text-xs font-medium">Deals</span>
              </Button>
              <Button variant="outline" className="h-auto py-3 flex-col gap-1.5 bg-slate-800 border-slate-700 hover:bg-slate-700 text-white" onClick={() => navigate('/clients')}>
                <Building2 className="w-5 h-5 text-slate-400" />
                <span className="text-xs font-medium">Clients</span>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Activity Section */}
      <div className={cn("grid gap-4 sm:gap-5", canSeeIncentives ? "lg:grid-cols-2" : "lg:grid-cols-1")}>
        {/* Recent Incentives (hidden if incentives disabled) */}
        {canSeeIncentives && (
        <Card className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700">
          <CardHeader className="flex flex-row items-center justify-between pb-3">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-slate-100 dark:bg-slate-700 flex items-center justify-center">
                <Award className="w-4 h-4 text-slate-600 dark:text-slate-400" />
              </div>
              <CardTitle className="text-base font-medium text-slate-900 dark:text-slate-100">Recent Incentives</CardTitle>
            </div>
            <Button variant="ghost" size="sm" className="text-sm text-slate-600 hover:text-slate-900" onClick={() => navigate('/incentives')}>
              View All <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </CardHeader>
          <CardContent className="pt-0">
            {recentIncentives.length > 0 ? (
              <div className="space-y-2">
                {recentIncentives.map((inc) => (
                  <div key={inc.id} className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-900 rounded-lg border border-slate-100 dark:border-slate-700">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-slate-900 dark:text-slate-100 truncate">{inc.company_name || 'Unknown'}</p>
                      <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{inc.metric_type} • {inc.period_month}</p>
                    </div>
                    <div className="text-right ml-3 flex-shrink-0">
                      <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">{formatCurrency(inc.amount_earned)}</p>
                      <span className={cn(
                        'text-xs font-medium px-2 py-0.5 rounded mt-1 inline-block',
                        inc.status === 'PAID' && 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
                        inc.status === 'PENDING' && 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
                        inc.status === 'APPROVED' && 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
                        inc.status === 'PAYABLE' && 'bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-300'
                      )}>
                        {inc.status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-slate-500">
                <Award className="w-10 h-10 mx-auto mb-2 text-slate-300 dark:text-slate-600" />
                <p className="text-sm font-medium">No incentives yet</p>
                <p className="text-xs text-slate-400">Your earnings will appear here</p>
              </div>
            )}
          </CardContent>
        </Card>
        )}

        {/* Projected Renewal Incentives (hidden if incentives disabled) */}
        {canSeeIncentives && (
        <Card className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                  <Clock className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <CardTitle className="text-base font-medium text-slate-900 dark:text-slate-100">
                    Projected Renewals
                  </CardTitle>
                  <p className="text-xs text-slate-500">Annual incentives by client anniversary</p>
                </div>
              </div>
              <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
                <SelectTrigger className="w-[90px] h-8 text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {availableYears.map(year => (
                    <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="mb-4 p-4 bg-slate-900 dark:bg-slate-950 rounded-lg">
              <p className="text-xs text-slate-400">Total Projected for {selectedYear}</p>
              <p className="text-2xl font-semibold text-white mt-1">{formatCurrency(totalProjectedRenewals)}</p>
            </div>
            
            {clients.length > 0 ? (
              <div className="grid grid-cols-4 sm:grid-cols-6 gap-2 max-h-[200px] overflow-y-auto">
                {projectedRenewals.map((proj, idx) => (
                  <div 
                    key={idx} 
                    className={cn(
                      "p-2 rounded-lg cursor-pointer transition-all text-center border",
                      proj.isCurrent ? "bg-blue-50 dark:bg-blue-900/30 border-blue-300 dark:border-blue-700" :
                      proj.isPast ? "bg-slate-50 dark:bg-slate-900 border-slate-100 dark:border-slate-800 opacity-50" :
                      "bg-slate-50 dark:bg-slate-900 border-slate-100 dark:border-slate-800 hover:border-slate-300"
                    )}
                    onClick={() => handleMonthClick(proj)}
                  >
                    <p className="text-xs text-slate-500 dark:text-slate-400">{MONTHS[proj.month]}</p>
                    <p className={cn(
                      "font-semibold text-xs mt-0.5",
                      proj.isCurrent ? "text-blue-700 dark:text-blue-300" : "text-slate-700 dark:text-slate-300"
                    )}>
                      {formatCurrency(proj.amount)}
                    </p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-6 text-slate-500">
                <IndianRupee className="w-10 h-10 mx-auto mb-2 text-slate-300 dark:text-slate-600" />
                <p className="text-sm font-medium">No clients yet</p>
                <p className="text-xs text-slate-400">Renewal projections will appear here</p>
              </div>
            )}
          </CardContent>
        </Card>
        )}
      </div>

      {/* Unrealized P&L / Future Liability (hidden if incentives disabled) */}
      {canSeeIncentives && (
      <Card className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700">
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-slate-100 dark:bg-slate-700 flex items-center justify-center">
                <BarChart3 className="w-4 h-4 text-slate-600 dark:text-slate-400" />
              </div>
              <div>
                <CardTitle className="text-base font-medium text-slate-900 dark:text-slate-100">
                  Future Earnings Projection
                </CardTitle>
                <p className="text-xs text-slate-500">Projected renewal payouts for next 10 years</p>
              </div>
            </div>
          </CardHeader>
          <CardContent className="pt-0">
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="sm:w-1/3 p-4 bg-slate-900 dark:bg-slate-950 rounded-lg">
                  <p className="text-xs text-slate-400">Next Year Projected</p>
                  <p className="text-2xl font-semibold text-white mt-1">{formatCurrency(projectionData.nextYearAmount)}</p>
                  <p className="text-xs text-slate-500 mt-2">Annual recurring incentive</p>
                </div>
              
                <div className="flex-1 max-h-[180px] overflow-y-auto">
                  <Table>
                      <TableHeader>
                          <TableRow className="border-slate-200 dark:border-slate-700">
                              <TableHead className="text-xs font-medium text-slate-500">Year</TableHead>
                              <TableHead className="text-xs font-medium text-slate-500 text-right">Projected Payout</TableHead>
                          </TableRow>
                      </TableHeader>
                      <TableBody>
                          {projectionData.table.map(row => (
                              <TableRow key={row.year} className="border-slate-100 dark:border-slate-800">
                                  <TableCell className="text-sm font-medium text-slate-700 dark:text-slate-300 py-2">{row.year}</TableCell>
                                  <TableCell className="text-sm text-right text-slate-600 dark:text-slate-400 py-2">
                                      {formatCurrency(row.amount)}
                                  </TableCell>
                              </TableRow>
                          ))}
                      </TableBody>
                  </Table>
                </div>
              </div>
          </CardContent>
      </Card>
      )}

      {/* Renewal Detail Modal */}
      <Dialog open={renewalDetailOpen} onOpenChange={setRenewalDetailOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-blue-600" />
              {selectedMonth?.monthName} - Renewal Breakdown
            </DialogTitle>
            <DialogDescription>
              Client-wise projected renewal incentives
            </DialogDescription>
          </DialogHeader>
          
          {selectedMonth && (
            <div className="space-y-4">
              <div className="p-3 bg-purple-50 dark:bg-purple-900/30 rounded-lg">
                <div className="flex justify-between items-center">
                  <span className="text-purple-600 dark:text-purple-400">Total for {selectedMonth.monthName}</span>
                  <span className="text-xl font-bold text-purple-700 dark:text-purple-300">{formatCurrency(selectedMonth.amount)}</span>
                </div>
              </div>
              
              {selectedMonth.clients.length > 0 ? (
                <div className="space-y-2 max-h-[300px] overflow-y-auto">
                  {selectedMonth.clients.map((client, idx) => (
                    <div key={idx} className="p-3 bg-slate-50 dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-700">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Building2 className="w-4 h-4 text-slate-400" />
                          <span className="font-medium text-slate-800 dark:text-slate-100">{client.company}</span>
                        </div>
                        <span className="font-bold text-emerald-600 dark:text-emerald-400">{formatCurrency(client.amount)}</span>
                      </div>
                      <div className="mt-2 text-xs text-slate-500 dark:text-slate-400 flex justify-between">
                        <span>MRR: {formatCurrency(client.mrr)}</span>
                        <span>× {client.renewalPct}% = {formatCurrency(client.amount)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6 text-slate-500">
                  <p>No clients with renewal incentives</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Call Outcome Modal */}
      <Dialog open={callOutcomeOpen} onOpenChange={(open) => {
        setCallOutcomeOpen(open);
        if (!open) {
          setCallOutcome({
            contact_method: 'PHONE_CALL',
            outcome: '',
            callback_date: '',
            callback_time: '',
            callback_notes: '',
            notes: ''
          });
        }
      }}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Phone className="w-5 h-5 text-blue-500" />
              Record Call Outcome
            </DialogTitle>
            <DialogDescription>
              {selectedLeadForCall && (
                <span className="font-medium text-slate-700">
                  {selectedLeadForCall.company_name} - {selectedLeadForCall.poc_name}
                </span>
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Contact Method */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700">Contact Method</label>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant={callOutcome.contact_method === 'PHONE_CALL' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setCallOutcome({...callOutcome, contact_method: 'PHONE_CALL'})}
                  className="flex-1"
                >
                  <Phone className="w-4 h-4 mr-2" />
                  Phone Call
                </Button>
                <Button
                  type="button"
                  variant={callOutcome.contact_method === 'WHATSAPP_VOICE' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setCallOutcome({...callOutcome, contact_method: 'WHATSAPP_VOICE'})}
                  className="flex-1"
                >
                  <MessageCircle className="w-4 h-4 mr-2" />
                  WhatsApp
                </Button>
              </div>
            </div>

            {/* Call Outcome */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700">Outcome *</label>
              <div className="grid grid-cols-2 gap-2">
                <Button
                  type="button"
                  variant={callOutcome.outcome === 'PICKED' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setCallOutcome({...callOutcome, outcome: 'PICKED'})}
                  className={cn("justify-start", callOutcome.outcome === 'PICKED' && "bg-green-600 hover:bg-green-700")}
                >
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  Picked
                </Button>
                <Button
                  type="button"
                  variant={callOutcome.outcome === 'NOT_PICKED' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setCallOutcome({...callOutcome, outcome: 'NOT_PICKED'})}
                  className={cn("justify-start", callOutcome.outcome === 'NOT_PICKED' && "bg-gray-600 hover:bg-gray-700")}
                >
                  <PhoneOff className="w-4 h-4 mr-2" />
                  Not Picked
                </Button>
                <Button
                  type="button"
                  variant={callOutcome.outcome === 'CALLBACK_LATER' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setCallOutcome({...callOutcome, outcome: 'CALLBACK_LATER'})}
                  className={cn("justify-start", callOutcome.outcome === 'CALLBACK_LATER' && "bg-amber-600 hover:bg-amber-700")}
                >
                  <PhoneForwarded className="w-4 h-4 mr-2" />
                  Callback Later
                </Button>
                <Button
                  type="button"
                  variant={callOutcome.outcome === 'DECLINED' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setCallOutcome({...callOutcome, outcome: 'DECLINED'})}
                  className={cn("justify-start", callOutcome.outcome === 'DECLINED' && "bg-red-600 hover:bg-red-700")}
                >
                  <XIcon className="w-4 h-4 mr-2" />
                  Declined
                </Button>
              </div>
            </div>

            {/* Callback Date/Time (conditional) */}
            {callOutcome.outcome === 'CALLBACK_LATER' && (
              <div className="space-y-3 p-4 bg-amber-50 rounded-lg border border-amber-200">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-sm font-medium text-amber-800">Callback Date *</label>
                    <Input
                      type="date"
                      value={callOutcome.callback_date}
                      onChange={(e) => setCallOutcome({...callOutcome, callback_date: e.target.value})}
                      min={new Date().toISOString().split('T')[0]}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-amber-800">Callback Time</label>
                    <Input
                      type="time"
                      value={callOutcome.callback_time}
                      onChange={(e) => setCallOutcome({...callOutcome, callback_time: e.target.value})}
                      className="mt-1"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-amber-800">Callback Notes</label>
                  <Textarea
                    value={callOutcome.callback_notes}
                    onChange={(e) => setCallOutcome({...callOutcome, callback_notes: e.target.value})}
                    placeholder="Notes for the callback..."
                    className="mt-1"
                    rows={2}
                  />
                </div>
              </div>
            )}

            {/* Notes */}
            <div>
              <label className="text-sm font-medium text-slate-700">Notes</label>
              <Textarea
                value={callOutcome.notes}
                onChange={(e) => setCallOutcome({...callOutcome, notes: e.target.value})}
                placeholder="Additional notes about the call..."
                className="mt-1"
                rows={2}
              />
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-2 pt-2">
              <Button
                variant="outline"
                onClick={() => setCallOutcomeOpen(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={handleRecordCallOutcome}
                disabled={!callOutcome.outcome || isSubmittingCall || (callOutcome.outcome === 'CALLBACK_LATER' && !callOutcome.callback_date)}
              >
                {isSubmittingCall ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                Save Outcome
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Admin/Manager Dashboard Component
const AdminDashboard = ({ stats, clients, teamConfigs, conversionConfigs, receivableStats, pendingCallbacks = [], onMarkCallbackDone, formatCurrency }) => {
  const navigate = useNavigate();

  // Calculate Projected Company Renewal Revenue
  const projectionData = useMemo(() => {
    // 1. Get Company Renewal Config
    const latestConvConfig = conversionConfigs.sort((a, b) => b.effective_month?.localeCompare(a.effective_month || ''))[0];
    let renewalMonthsCount = 2; // Default to 2 months (e.g., 1st & 2nd month of renewal year)
    
    if (latestConvConfig && latestConvConfig.renewal_year_claimable_months) {
        try {
            const months = JSON.parse(latestConvConfig.renewal_year_claimable_months);
            if (Array.isArray(months)) {
                renewalMonthsCount = months.length;
            }
        } catch (e) {
            console.error("Failed to parse renewal months", e);
        }
    }
    
    // 2. Calculate Annual Company Renewal Revenue
    let annualCompanyRevenue = 0;
    clients.forEach(client => {
        // Skip terminated clients (MRR = 0 means terminated)
        const isTerminated = client.mrr === 0 || client.is_terminated;
        if (isTerminated) return;
        
        if (client.status === 'WON_PAYMENT_RECEIVED' || client.status === 'IMPLEMENTATION_IN_PROGRESS' || client.status === 'LIVE_ACTIVE') {
            const mrr = client.mrr || client.plan_amount || 0;
            // Company earns MRR for EACH claimable renewal month
            const yearlyRevenue = mrr * renewalMonthsCount;
            annualCompanyRevenue += yearlyRevenue;
        }
    });

    // 3. Generate 10 Year Table
    const currentYear = new Date().getFullYear();
    const tenYearProjection = [];
    
    // Start from Next Year
    for (let i = 1; i <= 10; i++) {
        const year = currentYear + i;
        tenYearProjection.push({
            year: year,
            amount: annualCompanyRevenue
        });
    }

    return {
        nextYearAmount: annualCompanyRevenue,
        table: tenYearProjection,
        renewalMonthsCount
    };
  }, [clients, conversionConfigs]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-800 dark:text-slate-100">Admin Dashboard</h1>
        <p className="text-slate-500 dark:text-slate-400">Monitor team performance and company metrics</p>
      </div>

      {/* Company Revenue Cards (New) */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-blue-50 border-blue-200">
            <CardContent className="p-6">
                <p className="text-sm font-medium text-blue-600">Total Expected Revenue</p>
                <p className="text-2xl font-bold text-blue-700">{formatCurrency(receivableStats.total_expected || 0)}</p>
            </CardContent>
        </Card>
        <Card className="bg-purple-50 border-purple-200">
            <CardContent className="p-6">
                <p className="text-sm font-medium text-purple-600">Invoiced Revenue</p>
                <p className="text-2xl font-bold text-purple-700">{formatCurrency(receivableStats.total_invoiced || 0)}</p>
            </CardContent>
        </Card>
        <Card className="bg-emerald-50 border-emerald-200">
            <CardContent className="p-6">
                <p className="text-sm font-medium text-emerald-600">Realized (Paid) Revenue</p>
                <p className="text-2xl font-bold text-emerald-700">{formatCurrency(receivableStats.total_paid || 0)}</p>
            </CardContent>
        </Card>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500 dark:text-slate-400">Total SQDs</p>
                <p className="text-3xl font-bold text-slate-800 dark:text-slate-100">{stats.sqds_this_month || 0}</p>
                <p className="text-xs text-slate-400">This Month</p>
              </div>
              <Target className="w-10 h-10 text-blue-400" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500 dark:text-slate-400">Deals Won</p>
                <p className="text-3xl font-bold text-slate-800 dark:text-slate-100">{stats.deals_won_this_month || 0}</p>
                <p className="text-xs text-slate-400">This Month</p>
              </div>
              <TrendingUp className="w-10 h-10 text-emerald-400" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500 dark:text-slate-400">Open Leads</p>
                <p className="text-3xl font-bold text-slate-800 dark:text-slate-100">{stats.open_leads || 0}</p>
                <p className="text-xs text-slate-400">Total Active</p>
              </div>
              <FileText className="w-10 h-10 text-purple-400" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500 dark:text-slate-400">Scheduled Demos</p>
                <p className="text-3xl font-bold text-slate-800 dark:text-slate-100">{stats.demos_scheduled || 0}</p>
                <p className="text-xs text-slate-400">Upcoming</p>
              </div>
              <Calendar className="w-10 h-10 text-amber-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Team Incentives & Company Projections */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
            <CardHeader>
            <CardTitle className="text-lg font-semibold text-slate-800 dark:text-slate-100">Team Incentives Overview</CardTitle>
            </CardHeader>
            <CardContent>
            <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-amber-50 dark:bg-amber-900/30 rounded-lg border border-amber-200 dark:border-amber-800">
                <p className="text-sm text-amber-600 dark:text-amber-400">Pending Approval</p>
                <p className="text-2xl font-bold text-amber-700 dark:text-amber-300">{formatCurrency(stats.total_incentives_pending || 0)}</p>
                </div>
                <div className="p-4 bg-emerald-50 dark:bg-emerald-900/30 rounded-lg border border-emerald-200 dark:border-emerald-800">
                <p className="text-sm text-emerald-600 dark:text-emerald-400">Total Paid</p>
                <p className="text-2xl font-bold text-emerald-700 dark:text-emerald-300">{formatCurrency(stats.total_incentives_paid || 0)}</p>
                </div>
                <div className="p-4 bg-blue-50 dark:bg-blue-900/30 rounded-lg border border-blue-200 dark:border-blue-800">
                <p className="text-sm text-blue-600 dark:text-blue-400">Conversion Rate</p>
                <p className="text-2xl font-bold text-blue-700 dark:text-blue-300">{stats.conversion_rate || 0}%</p>
                </div>
                <div className="p-4 bg-purple-50 dark:bg-purple-900/30 rounded-lg border border-purple-200 dark:border-purple-800">
                <p className="text-sm text-purple-600 dark:text-purple-400">SQD Rate</p>
                <p className="text-2xl font-bold text-purple-700 dark:text-purple-300">{stats.sqd_rate || 0}%</p>
                </div>
            </div>
            </CardContent>
        </Card>

        {/* Projected Company Renewal Revenue */}
        <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
            <CardHeader>
                <CardTitle className="text-lg font-semibold text-slate-800 dark:text-slate-100 flex items-center gap-2">
                    <BarChart3 className="w-5 h-5 text-indigo-500" />
                    Projected Company Renewal Revenue
                </CardTitle>
                <CardDescription>Future annual revenue from renewals ({projectionData.renewalMonthsCount} months/year)</CardDescription>
            </CardHeader>
            <CardContent>
                <div className="mb-4 p-4 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-lg text-white">
                    <p className="text-sm text-indigo-100">Next Year Projected Revenue</p>
                    <p className="text-3xl font-bold">{formatCurrency(projectionData.nextYearAmount)}</p>
                </div>
                
                <div className="max-h-[200px] overflow-y-auto">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead className="w-[100px]">Year</TableHead>
                                <TableHead className="text-right">Projected Revenue</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {projectionData.table.map(row => (
                                <TableRow key={row.year}>
                                    <TableCell className="font-medium">{row.year}</TableCell>
                                    <TableCell className="text-right text-slate-600 dark:text-slate-300">
                                        {formatCurrency(row.amount)}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>
            </CardContent>
        </Card>
      </div>

      {/* TODO / Pending Callbacks */}
      {pendingCallbacks.length > 0 && (
        <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 border-l-4 border-l-amber-500">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-lg font-semibold text-slate-800 dark:text-slate-100 flex items-center gap-2">
              <Clock className="w-5 h-5 text-amber-500" />
              Pending Callbacks
              <Badge className="bg-amber-100 text-amber-700 ml-2">{pendingCallbacks.length}</Badge>
            </CardTitle>
            <Button variant="ghost" size="sm" onClick={() => navigate('/leads?status=CALLBACK_SCHEDULED')}>
              View All <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 max-h-[250px] overflow-y-auto">
              {pendingCallbacks.map((callback) => (
                <div 
                  key={callback.id} 
                  className={cn(
                    "flex items-center justify-between p-3 rounded-lg border cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-900",
                    callback.is_overdue ? "bg-red-50 border-red-200 dark:bg-red-900/20 dark:border-red-800" : 
                    callback.is_today ? "bg-amber-50 border-amber-200 dark:bg-amber-900/20 dark:border-amber-800" : 
                    "bg-slate-50 border-slate-200 dark:bg-slate-900 dark:border-slate-700"
                  )}
                  onClick={() => navigate(`/leads/${callback.id}`)}
                >
                  <div className="flex items-center gap-3">
                    <div className={cn(
                      "w-2 h-2 rounded-full",
                      callback.is_overdue ? "bg-red-500" : callback.is_today ? "bg-amber-500" : "bg-blue-500"
                    )} />
                    <div>
                      <p className="font-medium text-slate-800 dark:text-slate-100">{callback.company_name}</p>
                      <p className="text-sm text-slate-500">{callback.poc_name} • {callback.poc_mobile}</p>
                      {callback.callback_notes && (
                        <p className="text-xs text-slate-400 mt-1 truncate max-w-[300px]">{callback.callback_notes}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      <p className={cn(
                        "text-sm font-medium",
                        callback.is_overdue ? "text-red-600" : callback.is_today ? "text-amber-600" : "text-slate-600"
                      )}>
                        {callback.is_overdue ? 'Overdue' : callback.is_today ? 'Today' : new Date(callback.callback_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                      </p>
                      {callback.callback_time && <p className="text-xs text-slate-400">{callback.callback_time}</p>}
                    </div>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="h-8 w-8 p-0 text-emerald-600 hover:bg-emerald-50"
                      onClick={(e) => { e.stopPropagation(); onMarkCallbackDone && onMarkCallbackDone(callback.id); }}
                      title="Mark as Done"
                    >
                      <CheckCircle2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Quick Actions */}
      <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
        <CardHeader>
          <CardTitle className="text-lg font-semibold text-slate-800 dark:text-slate-100">Quick Actions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Button variant="outline" className="h-auto py-4 flex-col gap-2" onClick={() => navigate('/leads')}>
              <FileText className="w-6 h-6 text-purple-500" />
              <span>Manage Leads</span>
            </Button>
            <Button variant="outline" className="h-auto py-4 flex-col gap-2" onClick={() => navigate('/demos')}>
              <Calendar className="w-6 h-6 text-amber-500" />
              <span>View Demos</span>
            </Button>
            <Button variant="outline" className="h-auto py-4 flex-col gap-2" onClick={() => navigate('/incentives')}>
              <Award className="w-6 h-6 text-emerald-500" />
              <span>Team Incentives</span>
            </Button>
            <Button variant="outline" className="h-auto py-4 flex-col gap-2" onClick={() => navigate('/admin/incentive-config')}>
              <Target className="w-6 h-6 text-blue-500" />
              <span>Config Incentives</span>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

const DashboardPage = () => {
  const { user: currentUser } = useAuth();
  const { canSeeIncentives, formatMoney, showMoney, isAdmin: isPrivacyAdmin } = usePrivacy();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({});
  const [incentives, setIncentives] = useState([]);
  const [clients, setClients] = useState([]);
  const [teamConfigs, setTeamConfigs] = useState([]);
  const [conversionConfigs, setConversionConfigs] = useState([]); // Added for Admin calculation
  const [receivableStats, setReceivableStats] = useState({}); // Added for Admin
  const [pendingCallbacks, setPendingCallbacks] = useState([]); // TODO callbacks

  const isAdmin = currentUser?.role === 'ADMIN';
  const isManager = currentUser?.role === 'MANAGER' || currentUser?.role === 'SALES_MANAGER';
  const isAdminOrManager = isAdmin || isManager;

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        // Base requests for everyone
        const promises = [
          dashboardAPI.stats(),
          incentivesAPI.listRecords({ page_size: 100 }).catch(() => ({ data: [] })),
          clientsAPI.list({ page_size: 100 }).catch(() => ({ data: { clients: [] } })),
          incentivesAPI.listTeamConfigs().catch(() => ({ data: [] })),
          leadsAPI.getPendingCallbacks().catch(() => ({ data: [] })) // Fetch pending TODOs
        ];

        // Admin specific requests
        if (isAdmin) {
            promises.push(incentivesAPI.listConversionConfigs().catch(() => ({ data: [] })));
            promises.push(incentivesAPI.receivableStats().catch(() => ({ data: {} })));
        }

        const results = await Promise.all(promises);
        
        setStats(results[0].data || {});
        
        const incentivesData = results[1].data;
        const records = Array.isArray(incentivesData) ? incentivesData : (incentivesData?.records || []);
        
        // Filter incentives based on role
        if (!isAdminOrManager && currentUser?.id) {
          setIncentives(records.filter(r => r.user_id === currentUser.id));
        } else {
          setIncentives(records);
        }

        const clientsData = results[2].data;
        const clientsList = Array.isArray(clientsData) ? clientsData : (clientsData?.clients || []);
        // Filter clients based on role
        if (!isAdminOrManager && currentUser?.id) {
          setClients(clientsList.filter(c => c.primary_employee_id === currentUser.id));
        } else {
          setClients(clientsList);
        }

        setTeamConfigs(results[3].data || []);
        setPendingCallbacks(Array.isArray(results[4].data) ? results[4].data : []);

        if (isAdmin) {
            setConversionConfigs(results[5]?.data || []);
            setReceivableStats(results[6]?.data || {});
        }

      } catch (error) {
        console.error('Failed to fetch dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [isAdminOrManager, currentUser?.id, isAdmin]);

  // Handle marking callback as done
  const handleMarkCallbackDone = async (leadId) => {
    try {
      await leadsAPI.markCallbackDone(leadId);
      setPendingCallbacks(prev => prev.filter(c => c.id !== leadId));
    } catch (error) {
      console.error('Failed to mark callback as done:', error);
    }
  };

  // Refresh callbacks after recording call outcome
  const handleRefreshCallbacks = async () => {
    try {
      const result = await leadsAPI.getPendingCallbacks();
      setPendingCallbacks(Array.isArray(result.data) ? result.data : []);
    } catch (error) {
      console.error('Failed to refresh callbacks:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
        <span className="ml-2 text-slate-500">Loading dashboard...</span>
      </div>
    );
  }

  // Show role-based dashboard
  if (isAdminOrManager) {
    return (
        <AdminDashboard 
            stats={stats} 
            clients={clients} 
            teamConfigs={teamConfigs} 
            conversionConfigs={conversionConfigs}
            receivableStats={receivableStats}
            pendingCallbacks={pendingCallbacks}
            onMarkCallbackDone={handleMarkCallbackDone}
            formatCurrency={formatMoney}
        />
    );
  }

  return (
    <EmployeeDashboard 
      stats={stats} 
      incentives={incentives} 
      clients={clients} 
      teamConfigs={teamConfigs}
      pendingCallbacks={pendingCallbacks}
      onMarkCallbackDone={handleMarkCallbackDone}
      onRefreshCallbacks={handleRefreshCallbacks}
      canSeeIncentives={canSeeIncentives()}
      formatCurrency={formatMoney}
    />
  );
};

export default DashboardPage;
